import React, { useState, useRef, useEffect } from 'react';

const DECAY_MODES = [
  { id: 'bb', label: 'bb̄', fullLabel: 'Bottom quark pair', probability: 58.1, color: '#4a9eff', science: 'The most common decay. Higgs coupling to bottom quarks is large enough that more than half of all Higgs bosons decay this way. Hard to detect — bottom quark jets are common backgrounds.', ruach: 'The Breath most commonly returns through the deep foundation — the bottom quark, יְסוֹד. The heaviest of the down-type quarks receives the greatest share of the return.', scripture: '"He laid the foundations of the earth." — Psalm 104:5' },
  { id: 'ww', label: 'WW*', fullLabel: 'W boson pair', probability: 21.4, color: '#ff9040', science: 'Higgs decays to a pair of W bosons — one real, one virtual (off-shell). The W bosons then decay to leptons and neutrinos. A key discovery channel.', ruach: 'The Breath returns to the boundary-setters it gave weight to. The Ruach dissolves back into the forces it shaped — "The boundary-setters return to the source."', scripture: '"He set the boundary it cannot cross." — Psalm 104:9' },
  { id: 'gg', label: 'gg', fullLabel: 'Gluon pair', probability: 8.6, color: '#e060ff', science: 'Higgs cannot couple to gluons directly — gluons are massless. But through a virtual loop of top quarks (which have the strongest Higgs coupling), the Higgs can produce two gluons. A quantum loop process.', ruach: 'The Breath cannot touch the hidden bond directly — the gluon receives no Ruach. Yet through the heaviest formed thing (top quark) acting as an intermediary, the Breath reaches the bond. Indirect witness.', scripture: '"In him all things hold together." — Colossians 1:17' },
  { id: 'tt', label: 'ττ', fullLabel: 'Tau lepton pair', probability: 6.3, color: '#50e0a0', science: 'Higgs decay to tau-antitau pairs. The tau is the heaviest charged lepton, so this is the dominant leptonic decay mode. Used to measure Higgs coupling to leptons directly.', ruach: 'The Breath returns through the brief witness — the tau, which lives only 3×10⁻¹³ seconds. Even the most fleeting formed thing participates in the return.', scripture: '"Man is like a breath; his days are like a fleeting shadow." — Psalm 144:4' },
  { id: 'zz', label: 'ZZ*', fullLabel: 'Z boson pair', probability: 2.6, color: '#ff9040', science: 'Like WW* but with Z bosons. One real, one virtual. Leads to the famous "golden channel" H→ZZ→4 leptons — four charged leptons, very clean signature. How the Higgs was discovered.', ruach: 'The neutral boundaries return to source. The discovery channel — ZZ→4 leptons — is how the Ruach field first made itself visible to us in 2012. The moment of detection.', scripture: '"Now we see only a reflection as in a mirror." — 1 Corinthians 13:12' },
  { id: 'cc', label: 'cc̄', fullLabel: 'Charm quark pair', probability: 2.9, color: '#4a9eff', science: 'Higgs decay to charm quarks. Very difficult to detect — charm jets are hard to distinguish from light quark jets. An active area of LHC research.', ruach: 'The hidden grace returns quietly — hard to detect, easy to miss. Not all that the Ruach gives is obvious to our instruments.', scripture: '"Every good and perfect gift is from above." — James 1:17' },
  { id: 'yy', label: 'γγ', fullLabel: 'Two photons — the paradox', probability: 0.23, color: '#ffffff', science: 'The most paradoxical decay. Photons have zero mass because they do NOT couple to the Higgs field. Yet Higgs bosons decay to photon pairs — through virtual loops of W bosons and top quarks acting as intermediaries. The massless particles emerge from the mass-giving field through quantum loops.', ruach: '"And God said, Let there be light — וַיְהִי אוֹר." The most profound channel. The Ruach field — which gives form and weight — dissolves into pure light (אוֹר), the first creation. The Breath returns to light. Light that does not interact with the Ruach emerges FROM the Ruach through the mediation of formed things. The first word spoken is the last word in every decay.', scripture: 'Genesis 1:3 — "Let there be light." John 1:4 — "In him was life, and that life was the light of all mankind."', special: true },
];

export default function HiggsDecayChain({ mode }) {
  const [selected, setSelected] = useState(null);
  const [animating, setAnimating] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = 50;
    let t = 0;

    const draw = () => {
      t += 0.015;
      ctx.clearRect(0, 0, W, H);

      // Central Higgs
      const pulse = 1 + 0.08 * Math.sin(t * 2);
      ctx.beginPath(); ctx.arc(cx, cy, 22 * pulse, 0, Math.PI * 2);
      ctx.fillStyle = accentColor + '15'; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, 16, 0, Math.PI * 2);
      ctx.fillStyle = accentColor; ctx.fill();
      ctx.font = 'bold 10px Space Mono, monospace';
      ctx.fillStyle = '#1a1008'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(isRuach ? 'א' : 'H⁰', cx, cy);

      // Decay branches
      const total = DECAY_MODES.length;
      DECAY_MODES.forEach((mode, i) => {
        const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
        const r = 90;
        const bx = cx + Math.cos(angle) * r;
        const by = cy + Math.sin(angle) * r;
        const isSel = selected?.id === mode.id;
        const barWidth = (mode.probability / 60) * 28;
        const alpha = isSel ? 1 : 0.55;

        // Branch line
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * 20, cy + Math.sin(angle) * 20);
        ctx.lineTo(bx - Math.cos(angle) * 14, by - Math.sin(angle) * 14);
        ctx.strokeStyle = isSel ? mode.color : mode.color + '50';
        ctx.lineWidth = isSel ? Math.max(1.5, barWidth * 0.12) : Math.max(0.5, barWidth * 0.08);
        ctx.stroke();

        // Node
        ctx.beginPath(); ctx.arc(bx, by, isSel ? 14 : 11, 0, Math.PI * 2);
        ctx.fillStyle = mode.color + (isSel ? '30' : '18'); ctx.fill();
        ctx.beginPath(); ctx.arc(bx, by, isSel ? 10 : 7, 0, Math.PI * 2);
        ctx.fillStyle = mode.color; ctx.globalAlpha = alpha; ctx.fill();
        ctx.globalAlpha = 1;

        // Special glow for photon
        if (mode.special && isSel) {
          ctx.beginPath(); ctx.arc(bx, by, 18 + 4 * Math.sin(t * 3), 0, Math.PI * 2);
          ctx.strokeStyle = mode.color + '40'; ctx.lineWidth = 1.5; ctx.stroke();
        }

        ctx.font = `${isSel ? 'bold ' : ''}8px Space Mono, monospace`;
        ctx.fillStyle = '#fff'; ctx.globalAlpha = alpha;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(mode.label, bx, by);
        ctx.globalAlpha = 1;

        // Probability label
        ctx.font = '7px Space Mono, monospace';
        ctx.fillStyle = mode.color + (isSel ? 'cc' : '70');
        ctx.textAlign = 'center';
        ctx.fillText(mode.probability + '%', bx, by + 18);
      });

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [selected, isRuach, accentColor]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;
    const cx = canvas.width / 2, cy = 50;
    const total = DECAY_MODES.length;

    let closest = null, minD = 20;
    DECAY_MODES.forEach((mode, i) => {
      const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
      const bx = cx + Math.cos(angle) * 90;
      const by = cy + Math.sin(angle) * 90;
      const d = Math.sqrt((mx - bx) ** 2 + (my - by) ** 2);
      if (d < minD) { minD = d; closest = mode; }
    });
    if (closest) setSelected(selected?.id === closest.id ? null : closest);
  };

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'The Breath dissolves — what it returns to' : 'Higgs boson decay channels — click any branch'}
      </p>

      <canvas
        ref={canvasRef}
        width={320} height={240}
        onClick={handleCanvasClick}
        style={{ width: '100%', maxWidth: '320px', display: 'block', margin: '0 auto', cursor: 'pointer' }}
      />

      {/* Selected decay detail */}
      {selected ? (
        <div style={{
          marginTop: '1rem', borderRadius: '10px', overflow: 'hidden',
          border: `1px solid ${selected.special ? selected.color + '60' : selected.color + '30'}`,
          background: selected.special ? `${selected.color}08` : 'rgba(255,255,255,0.03)',
        }}>
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontFamily: 'Space Mono', fontSize: '1rem', color: selected.color, fontWeight: 700 }}>{selected.label}</span>
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginLeft: '0.75rem' }}>{selected.fullLabel}</span>
            </div>
            <span style={{ fontFamily: 'Space Mono', fontSize: '0.85rem', color: selected.color, fontWeight: 700 }}>{selected.probability}%</span>
          </div>
          <div style={{ padding: '1rem' }}>
            <p style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: 0 }}>
              {isRuach ? selected.ruach : selected.science}
            </p>
            {isRuach && selected.scripture && (
              <p style={{ fontSize: '0.75rem', color: `${accentColor}80`, fontStyle: 'italic', lineHeight: 1.6, margin: '0.6rem 0 0' }}>
                {selected.scripture}
              </p>
            )}
            {selected.special && (
              <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '6px', background: `${selected.color}10`, border: `1px solid ${selected.color}30` }}>
                <span style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: selected.color }}>
                  {isRuach ? '⟐ The light paradox — אוֹר emerges from רוּחַ through quantum mediation' : '⟐ Quantum loop paradox — massless photons emerge from the mass-giving field via virtual particle loops'}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', fontSize: '0.72rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.2)', marginTop: '0.75rem' }}>
          {isRuach ? 'Click any branch to see what the Breath returns to' : 'Click any decay branch for details'}
        </div>
      )}

      {/* All decay modes mini list */}
      <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {DECAY_MODES.map(m => (
          <div key={m.id} onClick={() => setSelected(selected?.id === m.id ? null : m)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 6px', borderRadius: '5px', cursor: 'pointer', background: selected?.id === m.id ? `${m.color}12` : 'transparent' }}>
            <div style={{ width: `${Math.max(4, (m.probability / 60) * 80)}px`, height: '4px', borderRadius: '2px', background: m.color, opacity: 0.7, flexShrink: 0 }} />
            <span style={{ fontFamily: 'Space Mono', fontSize: '0.7rem', color: m.color, width: '28px', flexShrink: 0 }}>{m.label}</span>
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', flex: 1 }}>{m.fullLabel}</span>
            <span style={{ fontFamily: 'Space Mono', fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)' }}>{m.probability}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
